import csv from "./metricChartData.csv"
import { parseCsv } from "@/lib/csv"
import { CAPABILITY_SERIES } from "./capabilitySeries"
import { getStaticPowerShareSeries } from "./powerShareSeries"
import type { MetricId, MetricScenarioSeries, SeriesPoint } from "./metricDetailsTypes"

// Chart series per metric. The hand-authored metrics live in metricChartData.csv
// (long format: metric,series,year,value; series ∈ observed | projection |
// branchA | branchD), parsed at load. Capability and power are sampled LIVE
// from the HUD's own sources instead (see capabilitySeries / powerShareSeries)
// and merged in below, so they can't drift — and income, alignment, safetyTax,
// employment, and labor are overridden at render by the server-parsed
// explainer model series (loadExplainerModelSeries), leaving their CSV rows as
// a fallback only.
const emptySeries = (): MetricScenarioSeries => ({
  observed: [],
  projection: [],
  branches: { A: [], D: [] },
})

const bucketFor = (series: MetricScenarioSeries, name: string): SeriesPoint[] | null => {
  switch (name) {
    case "observed": return series.observed
    case "projection": return series.projection
    case "branchA": return series.branches.A
    case "branchD": return series.branches.D
    default: return null
  }
}

const parse = (): Record<string, MetricScenarioSeries> => {
  const out: Record<string, MetricScenarioSeries> = {}
  const rows = parseCsv(csv.trim()).slice(1) // drop header
  for (const [metric, series, year, value] of rows) {
    if (!metric || !series) continue
    const bucket = bucketFor((out[metric] ??= emptySeries()), series)
    if (bucket) bucket.push({ year: Number(year), value: Number(value) })
  }
  // Callers rely on ascending years regardless of CSV row order.
  for (const s of Object.values(out)) {
    for (const pts of [s.observed, s.projection, s.branches.A, s.branches.D]) pts.sort((a, b) => a.year - b.year)
  }
  return out
}

export const CHART_SERIES = {
  ...(parse() as Record<Exclude<MetricId, "capability" | "power">, MetricScenarioSeries>),
  capability: CAPABILITY_SERIES,
  // Getter so the power derivation runs on first access (a details panel
  // opening), not at module load for every playbook reader.
  get power(): MetricScenarioSeries {
    return getStaticPowerShareSeries()
  },
}
